<script setup>
import { ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import MenuItem from 'dashboard/components/widgets/conversation/contextMenu/menuItem.vue';
import AddPopup from './AddPopup.vue';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  contextMenuPosition: {
    type: Object,
    default: () => ({}),
  },
  contactReason: {
    type: Object,
    default: () => ({}),
  },
  contactReasonPath: {
    type: Array,
    default: () => [],
  },
  level: {
    type: Number,
    required: true,
  },
});
const emit = defineEmits(['close', 'created']);
const store = useStore();
const { t } = useI18n();

const showDeleteModal = ref(false);
const showAddPopup = ref(false);

const closeDeleteModal = () => {
  showDeleteModal.value = false;
};

const handleClose = e => {
  emit('close', e);
};

const openDeleteModal = () => {
  showDeleteModal.value = true;
  handleClose();
};

const openAddPopup = () => {
  showAddPopup.value = true;
};

const hideAddPopup = () => {
  showAddPopup.value = false;
};

const confirmDeletion = async () => {
  try {
    await store.dispatch('contactReasons/delete', {
      contactReasonPath: props.contactReasonPath,
      data: props.contactReason,
    });
    emit('close');
    useAlert(t('CONTACT_REASONS.API.DELETE.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('CONTACT_REASONS.API.DELETE.ERROR_MESSAGE'));
  }
};

const handleAddChildren = () => {
  openAddPopup();
  handleClose();
};
</script>

<template>
  <div class="context-menu" @click.stop>
    <woot-delete-modal
      v-if="showDeleteModal"
      v-model:show="showDeleteModal"
      class="context-menu--delete-modal"
      :on-close="closeDeleteModal"
      :on-confirm="confirmDeletion"
      :title="$t('CONTACT_REASONS.CONTEXT_MENU.DELETE_CONFIRMATION.TITLE')"
      :message="$t('CONTACT_REASONS.CONTEXT_MENU.DELETE_CONFIRMATION.MESSAGE')"
      :confirm-text="
        $t('CONTACT_REASONS.CONTEXT_MENU.DELETE_CONFIRMATION.DELETE')
      "
      :reject-text="
        $t('CONTACT_REASONS.CONTEXT_MENU.DELETE_CONFIRMATION.CANCEL')
      "
    />
    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddPopup
        :contact-reason-path="contactReasonPath"
        @close="hideAddPopup"
        @created="emit('created', $event)"
      />
    </woot-modal>
    <ContextMenu
      v-if="isOpen"
      :x="contextMenuPosition.x"
      :y="contextMenuPosition.y"
      @close="handleClose"
    >
      <div class="menu-container">
        <MenuItem
          v-if="level < 3"
          :option="{
            icon: 'add',
            label: $t('CONTACT_REASONS.CONTEXT_MENU.ADD_CHILDREN'),
          }"
          variant="icon"
          @click.stop="handleAddChildren"
        />
        <MenuItem
          :option="{
            icon: 'delete',
            label: $t('CONTACT_REASONS.CONTEXT_MENU.DELETE'),
          }"
          variant="icon"
          @click.stop="openDeleteModal"
        />
      </div>
    </ContextMenu>
  </div>
</template>

<style lang="scss" scoped>
.menu-container {
  @apply p-1 bg-n-slate-4 shadow-xl rounded-md;

  hr:first-child {
    @apply hidden;
  }

  hr {
    @apply m-1 border-b border-solid border-n-strong;
  }
}
</style>
